/**
 * useCollapsibleState - Hook for accordion/collapsible section state
 *
 * Manages expand/collapse state for L3 (DramaX) accordion sections.
 * Phase 3.4: Optional Accordions for L3
 *
 * A/B Test Decision Criteria:
 * - >40% engagement AND depth improvement → KEEP accordions
 * - Degraded engagement → REMOVE accordions
 */

import { useState, useCallback, useMemo } from 'react';
import { LayoutAnimation, Platform, AccessibilityInfo } from 'react-native';

export interface UseCollapsibleStateOptions {
  /** Initial expanded state (default: true for most sections) */
  defaultExpanded?: boolean;
  /** Whether to animate expand/collapse (respects reduced motion) */
  animated?: boolean;
  /** Section identifier for analytics */
  sectionId?: string;
}

export interface UseCollapsibleStateReturn {
  /** Whether section is expanded */
  isExpanded: boolean;
  /** Toggle expand/collapse */
  toggle: () => void;
  /** Expand section */
  expand: () => void;
  /** Collapse section */
  collapse: () => void;
  /** Props for accessibility (spread on Pressable) */
  accessibilityProps: {
    accessibilityState: { expanded: boolean };
    accessibilityRole: 'button';
  };
}

/**
 * Hook for managing collapsible section state (L3 only)
 *
 * @example
 * const { isExpanded, toggle, accessibilityProps } = useCollapsibleState({
 *   defaultExpanded: true,
 *   animated: true,
 *   sectionId: 'patterns',
 * });
 *
 * <Pressable onPress={toggle} {...accessibilityProps}>
 *   <Text>Patterns ({count})</Text>
 * </Pressable>
 * {isExpanded && <PatternsList />}
 */
export function useCollapsibleState({
  defaultExpanded = true,
  animated = true,
  sectionId,
}: UseCollapsibleStateOptions = {}): UseCollapsibleStateReturn {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [reducedMotion, setReducedMotion] = useState(false);

  // Check reduced motion preference on mount
  useState(() => {
    if (Platform.OS !== 'web') {
      AccessibilityInfo.isReduceMotionEnabled().then(setReducedMotion);
    }
  });

  const animateIfEnabled = useCallback(() => {
    if (animated && !reducedMotion && Platform.OS !== 'web') {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    }
  }, [animated, reducedMotion]);

  const toggle = useCallback(() => {
    animateIfEnabled();
    setIsExpanded((prev) => !prev);
  }, [animateIfEnabled]);

  const expand = useCallback(() => {
    if (!isExpanded) {
      animateIfEnabled();
      setIsExpanded(true);
    }
  }, [isExpanded, animateIfEnabled]);

  const collapse = useCallback(() => {
    if (isExpanded) {
      animateIfEnabled();
      setIsExpanded(false);
    }
  }, [isExpanded, animateIfEnabled]);

  const accessibilityProps = useMemo(
    () => ({
      accessibilityState: { expanded: isExpanded },
      accessibilityRole: 'button' as const,
    }),
    [isExpanded]
  );

  return useMemo(
    () => ({
      isExpanded,
      toggle,
      expand,
      collapse,
      accessibilityProps,
    }),
    [isExpanded, toggle, expand, collapse, accessibilityProps]
  );
}

/** Default expanded state per section type */
export const COLLAPSIBLE_DEFAULTS = {
  patterns: true, // Expanded by default (L2+ content, users want to see)
  transformations: false, // Collapsed by default (heavy content, L3 only)
} as const;
